import { useQueryClient } from "@tanstack/react-query";
import {
  CreateBase,
  Form,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useResourceContext,
  useTranslate,
  useUpdate,
  type Identifier,
  type RaRecord,
} from "ra-core";
import { useFormContext } from "react-hook-form";
import { SaveButton } from "@/components/admin/form";
import { noteDateForCreate } from "@/lib/safeDate";
import { cn } from "@/lib/utils";

import { AiNoteExtractButton } from "./AiNoteExtractButton";
import { keepCreatedNote } from "./createdNoteCache";
import { foreignKeyMapping } from "./foreignKeyMapping";
import { NoteInputs } from "./NoteInputs";
import { parentPatchFromNote } from "./noteModel";

export const NoteCreate = ({
  reference,
  showStatus,
  className,
}: {
  reference: "contacts" | "deals";
  showStatus?: boolean;
  className?: string;
}) => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const { identity } = useGetIdentity();
  const queryClient = useQueryClient();

  if (!record || !identity) return null;

  const defaultStatus = reference === "contacts" ? record.status : undefined;
  const cancelNoteLists = async () => {
    if (!resource) return;
    await queryClient.cancelQueries({
      queryKey: [resource, "getInfiniteList"],
    });
    await queryClient.cancelQueries({ queryKey: [resource, "getList"] });
  };

  return (
    <CreateBase
      resource={resource}
      redirect={false}
      mutationOptions={{ onMutate: cancelNoteLists }}
    >
      <Form>
        <div className={cn("space-y-3", className)}>
          <NoteInputs defaultStatus={defaultStatus} showStatus={showStatus} />
          <NoteCreateButton
            defaultStatus={defaultStatus}
            record={record}
            reference={reference}
          />
        </div>
      </Form>
    </CreateBase>
  );
};

const NoteCreateButton = ({
  defaultStatus,
  reference,
  record,
}: {
  defaultStatus?: string;
  reference: "contacts" | "deals";
  record: RaRecord<Identifier>;
}) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { reset } = useFormContext();
  const resource = useResourceContext();
  const queryClient = useQueryClient();

  if (!record || !identity) return null;

  const resetValues: {
    date: string;
    text: string;
    attachments: null;
    status?: string;
  } = {
    date: "",
    text: "",
    attachments: null,
  };

  const handleSuccess = async (data: any) => {
    if (reference === "contacts") {
      resetValues.status = data.status ?? defaultStatus;
    }

    const saved = {
      ...data,
      date: data?.date ?? null,
      ...(reference === "contacts"
        ? { contact_id: data?.contact_id ?? record.id }
        : { deal_id: data?.deal_id ?? record.id }),
    };
    // Empty string clears the controlled textarea. null leaves the typed text
    // on screen, so the composer still looks unsaved.
    reset(resetValues, { keepValues: false });
    if (resource) {
      // Show the card before the refetch. invalidateQueries (also fired by
      // useCreate when the mutation settles) can replace the list with a read
      // that started too early and omitted this row.
      keepCreatedNote(queryClient, resource, saved);
      await queryClient.invalidateQueries({
        queryKey: [resource, "getInfiniteList"],
      });
      await queryClient.invalidateQueries({ queryKey: [resource, "getList"] });
      keepCreatedNote(queryClient, resource, saved);
    }
    const patch = parentPatchFromNote({
      reference,
      status: data?.status,
    });
    if (patch) {
      update(reference, {
        id: (record && record.id) as unknown as Identifier,
        data: patch,
        previousData: record,
      });
    }
    notify("resources.notes.added", {
      messageArgs: {
        _: "Note added",
      },
    });
  };

  return (
    <div className="flex min-w-0 flex-wrap justify-end gap-2 max-md:scroll-mb-[calc(var(--spacing)*18+16px+env(safe-area-inset-bottom,0px))]">
      {reference === "contacts" ? (
        <AiNoteExtractButton contactId={record.id} />
      ) : null}
      <SaveButton
        type="button"
        label={translate("resources.notes.action.add_this")}
        transform={(data) => ({
          ...data,
          [foreignKeyMapping[reference]]: record.id,
          sales_id: identity.id,
          date: noteDateForCreate(data.date),
        })}
        mutationOptions={{
          onSuccess: handleSuccess,
        }}
      />
    </div>
  );
};
